import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import ProductForm from './ProductForm';
import FeaturedManager from './FeaturedManager';
import BlogManager from './BlogManager';
import FavouritesAnalytics from './FavouritesAnalytics'; // New import
import './AdminDashboard.css';
import './BlogManager.css';

const TAG_COLORS = {
  NEW:     '#171717',
  LIMITED: '#da291c',
  SALE:    '#e8b400',
  HOT:     '#ff6b35',
};

export default function AdminDashboard({ onLogout }) {
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [view,     setView]     = useState('list');
  const [editing,  setEditing]  = useState(null);
  const [search,   setSearch]   = useState('');
  const [deleting, setDeleting] = useState(null);

  const fetchProducts = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('products')
      .select('*')
      .order('created_at', { ascending: false });
    setProducts(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchProducts(); }, []);

  const handleDelete = async (id) => {
    if (deleting !== id) { setDeleting(id); return; }
    await supabase.from('products').delete().eq('id', id);
    setDeleting(null);
    fetchProducts();
  };

  const toggleStock = async (product) => {
    await supabase
      .from('products')
      .update({ in_stock: !product.in_stock })
      .eq('id', product.id);
    fetchProducts();
  };

  const handleSave = () => {
    setView('list');
    setEditing(null);
    fetchProducts();
  };

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.brand.toLowerCase().includes(search.toLowerCase())
  );

  const Sidebar = () => (
    <aside className="ad__sidebar">
      <div className="ad__sidebar-logo">
        <span className="ad__logo-sa">SA</span>
        <span className="ad__logo-sep">—</span>
        <span className="ad__logo-dropped">DROPPED</span>
      </div>
      <p className="ad__sidebar-label">Admin Portal</p>

      <nav className="ad__nav">
        <button
          className={`ad__nav-link${view === 'list' ? ' ad__nav-link--active' : ''}`}
          onClick={() => { setView('list'); setEditing(null); }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2"/>
            <line x1="8" y1="21" x2="16" y2="21"/>
            <line x1="12" y1="17" x2="12" y2="21"/>
          </svg>
          Products
        </button>

        <button
          className={`ad__nav-link${view === 'featured' ? ' ad__nav-link--active' : ''}`}
          onClick={() => setView('featured')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
          Featured
        </button>

        <button
          className={`ad__nav-link${view === 'blog' ? ' ad__nav-link--active' : ''}`}
          onClick={() => setView('blog')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10 9 9 9 8 9"/>
          </svg>
          Blog
        </button>

        {/* New Favourites Analytics Nav Link */}
        <button
          className={`ad__nav-link${view === 'favourites' ? ' ad__nav-link--active' : ''}`}
          onClick={() => setView('favourites')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
          </svg>
          Favourites
        </button>

        <a href="/" className="ad__nav-link" target="_blank" rel="noreferrer">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
            <polyline points="15 3 21 3 21 9"/>
            <line x1="10" y1="14" x2="21" y2="3"/>
          </svg>
          View Site
        </a>
      </nav>

      <button className="ad__logout" onClick={onLogout}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
          <polyline points="16 17 21 12 16 7"/>
          <line x1="21" y1="12" x2="9" y2="12"/>
        </svg>
        Sign Out
      </button>
    </aside>
  );

  /* ── FORM VIEW ── */
  if (view === 'create' || view === 'edit') {
    return (
      <div className="ad">
        <Sidebar />
        <div className="ad__form-wrap">
          <div className="ad__topbar">
            <button className="ad__back" onClick={() => { setView('list'); setEditing(null); }}>
              ← Back to Products
            </button>
          </div>
          <div className="ad__content">
            <ProductForm
              existing={editing}
              onSave={handleSave}
              onCancel={() => { setView('list'); setEditing(null); }}
            />
          </div>
        </div>
      </div>
    );
  }

  /* ── FEATURED VIEW ── */
  if (view === 'featured') {
    return (
      <div className="ad">
        <Sidebar />
        <main className="ad__main">
          <FeaturedManager products={products} onUpdate={fetchProducts} />
        </main>
      </div>
    );
  }

  /* ── BLOG VIEW ── */
  if (view === 'blog') {
    return (
      <div className="ad">
        <Sidebar />
        <main className="ad__main">
          <BlogManager />
        </main>
      </div>
    );
  }

  /* ── FAVOURITES ANALYTICS VIEW ── */
  if (view === 'favourites') {
    return (
      <div className="ad">
        <Sidebar />
        <main className="ad__main">
          <FavouritesAnalytics />
        </main>
      </div>
    );
  }

  /* ── LIST VIEW ── */
  return (
    <div className="ad">
      <Sidebar />

      <main className="ad__main">
        <div className="ad__header">
          <div>
            <h1 className="ad__title">Products</h1>
            <p className="ad__subtitle">{products.length} listings total</p>
          </div>
          <button className="ad__add-btn" onClick={() => setView('create')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add Product
          </button>
        </div>

        <div className="ad__search-wrap">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7.5"/>
            <line x1="16.5" y1="16.5" x2="22" y2="22"/>
          </svg>
          <input
            className="ad__search"
            placeholder="Search by name or brand..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="ad__loading">Loading products...</div>
        ) : filtered.length === 0 ? (
          <div className="ad__empty">
            <p>No products found.</p>
            <button onClick={() => setView('create')}>Create your first listing</button>
          </div>
        ) : (
          <div className="ad__table-wrap">
            <table className="ad__table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Brand</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Tag</th>
                  <th>Stock</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id}>
                    <td>
                      <div className="ad__product-cell">
                        {p.images?.[0] && (
                          <img src={p.images[0]} alt={p.name} className="ad__thumb" />
                        )}
                        <div>
                          <p className="ad__product-name">{p.name}</p>
                          <p className="ad__product-color">{p.colorway}</p>
                        </div>
                      </div>
                    </td>
                    <td className="ad__cell-muted">{p.brand}</td>
                    <td className="ad__cell-muted">{p.category}</td>
                    <td className="ad__cell-price">R {p.price?.toLocaleString()}</td>
                    <td>
                      {p.tag ? (
                        <span className="ad__tag" style={{ background: TAG_COLORS[p.tag] || '#333' }}>
                          {p.tag}
                        </span>
                      ) : (
                        <span className="ad__cell-muted">—</span>
                      )}
                    </td>
                    <td>
                      <button
                        className={`ad__stock-btn${p.in_stock ? ' ad__stock-btn--in' : ' ad__stock-btn--out'}`}
                        onClick={() => toggleStock(p)}
                        title="Click to toggle"
                      >
                        {p.in_stock ? 'In Stock' : 'Out of Stock'}
                      </button>
                    </td>
                    <td>
                      <div className="ad__actions">
                        <button
                          className="ad__action-btn ad__action-btn--edit"
                          onClick={() => { setEditing(p); setView('edit'); }}
                          title="Edit"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                          Edit
                        </button>
                        <button
                          className={`ad__action-btn${deleting === p.id ? ' ad__action-btn--confirm' : ' ad__action-btn--delete'}`}
                          onClick={() => handleDelete(p.id)}
                          title={deleting === p.id ? 'Click again to confirm' : 'Delete'}
                        >
                          {deleting === p.id ? '⚠ Confirm' : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                              <path d="M10 11v6"/>
                              <path d="M14 11v6"/>
                              <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                            </svg>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}